import React from "react";
import { Link } from "react-router-dom";
import "./UnderstandAiAstrology.css";

const STEPS = [
  {
    title: "Enter Your Birth Details",
    body: "Provide your date, exact time, and place of birth. Aura AI uses this information to generate your Kundli and understand your unique Vedic birth chart.",
  },
  {
    title: "Generate Your Free Kundli",
    body: "Your Free Kundli is created using your birth details and becomes the foundation for every conversation. Aura AI studies your planetary positions, houses, Dashas, and other key elements of Vedic astrology to deliver insights that are unique to you.",
  },
  {
    title: "Chat with Your AI Astrologer",
    body: "Whether you're exploring career astrology, marriage astrology, relationships, finances, or personal growth, Aura AI explains your birth chart in simple, conversational language that's easy to understand.",
  },
  {
    title: "Continue the Conversation",
    body: "Life keeps changing, and so do your questions. Continue chatting with Aura AI whenever you need guidance or want to understand a new situation through AI astrology.",
  },
];

const TOPICS = [
  {
    title: "Career & Job",
    body: "Thinking about changing jobs, preparing for an interview, or planning your next move? Explore career astrology insights to better understand opportunities, strengths, and important phases in your professional journey.",
  },
  {
    title: "Love & Relationships",
    body: "Understand relationship patterns, emotional compatibility, and the dynamics that shape your connections. Whether you're single or in a relationship, Aura AI helps you explore these questions with greater clarity.",
  },
  {
    title: "Marriage",
    body: "Thinking about marriage or wondering about compatibility? Explore marriage astrology, Kundli matching, and relationship compatibility through your birth chart.",
  },
  {
    title: "Money & Wealth",
    body: "Understand financial patterns, wealth opportunities, and important phases that may influence your financial journey.",
  },
  {
    title: "Health & Well-being",
    body: "Explore your birth chart to better understand periods that may affect your energy, balance, and overall well-being through the lens of Vedic astrology.",
  },
  {
    title: "Personality & Life Path",
    body: "Discover your natural strengths, communication style, motivations, and life tendencies through your Kundli.",
  },
];

const REASONS = [
  "Personalized insights based on your birth details",
  "Generate your Free Kundli in minutes",
  "Chat naturally with your AI astrologer",
  "Easy-to-understand explanations rooted in Vedic astrology",
  "Explore your Horoscope, Rashifal, Kundli matching, and more",
  "Guidance for career, marriage, relationships, money, and life decisions",
  "Private and secure conversations",
  "Available 24x7, in English and Hindi",
];

const UnderstandAiAstrology: React.FC = () => (
  <section className="uaa" aria-labelledby="uaa-heading">
    <div className="uaa-inner">
      <p className="uaa-eyebrow">Understand AI Astrology</p>
      <h2 className="uaa-heading" id="uaa-heading">
        AI Astrology, Made Personal with Aura AI
      </h2>

      <div className="uaa-card">
        <p>
          Life rarely comes with simple answers. Whether you're thinking about
          your next career move, navigating a relationship, planning your
          future, or simply looking for clarity, AI astrology makes it easier to
          explore life's questions in a more personal way.
        </p>
        {/* The only in-content links on the homepage. Every other internal link
            on this site sits in the nav or the footer, which is sitewide
            boilerplate and heavily discounted. A link inside body copy carries
            far more weight, and the homepage is the page with authority to pass
            on. The anchor text is the sentence that was already written, not a
            phrase bolted on to hold a link. */}
        <p>
          Aura AI combines the timeless wisdom of Vedic astrology with
          intelligent conversations to help you understand yourself better.
          Simply enter your birth details to generate your{" "}
          <Link to="/free-kundli">free kundli</Link>, explore your Kundli, and
          chat with an <Link to="/ai-astrologer">AI astrologer</Link> whenever
          you have a question. If you would rather talk first and read charts
          later, start a{" "}
          <Link to="/talk-to-astrologer">free astrology chat</Link> any time,
          day or night.
        </p>
        {/* Third in-content anchor, added because the first two proved the
            point: /ai-astrologer and /free-kundli each got one on 1 Sep, and
            /ai-astrologer is now the second-best page on the site at 230
            clicks. /kundli-matching had no in-content link anywhere and sat at
            29 clicks over the same 28 days, despite gun milan being one of the
            highest-volume astrology queries in India. Anchor text is the head
            term, in a sentence that reads as it would have anyway. */}
        <p>
          Marriage is its own question, so it has its own tool. Run{" "}
          <Link to="/kundli-matching">kundli matching</Link> to score the full
          Ashtakoot gun milan for two charts out of 36 gunas, with Mangal and
          Nadi dosha checked in both.
        </p>
        <p>
          No confusing charts. No complicated astrology terms. Just meaningful
          conversations that help you understand yourself, recognize patterns,
          and reflect on the possibilities ahead.
        </p>
      </div>

      <div className="uaa-card">
        <h3 className="uaa-h3">How AI Astrology Works</h3>
        <ol className="uaa-steps">
          {STEPS.map((step, i) => (
            <li className="uaa-step" key={step.title}>
              <span className="uaa-step-num" aria-hidden="true">
                {i + 1}
              </span>
              <div>
                <h4 className="uaa-step-title">{step.title}</h4>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="uaa-card">
        <h3 className="uaa-h3">What Can You Ask Your AI Astrologer?</h3>
        <p className="uaa-lead">
          Every Kundli tells a different story, which means every conversation
          with Aura AI is built around your own birth chart, not generic
          predictions.
        </p>
        <div className="uaa-topics">
          {TOPICS.map((topic) => (
            <div className="uaa-topic" key={topic.title}>
              <h4 className="uaa-topic-title">{topic.title}</h4>
              <p>{topic.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="uaa-card">
        <h3 className="uaa-h3">Understand Your Kundli</h3>
        <p>
          Your Kundli, also known as a Vedic birth chart, is a map of the
          planetary positions at the exact time and place you were born. It
          helps you better understand your personality, relationships, career,
          marriage, finances, and important phases of life.
        </p>
        <p>
          Aura AI explains your birth chart in clear, conversational language,
          making AI astrology easy to understand whether you're just getting
          started or have been exploring Vedic astrology for years.
        </p>
        <p>
          You can also ask Aura AI about topics like Mahadasha, Rahu Mahadasha,
          Sade Sati, Rahu Ketu, Mangal Dosha, planetary combinations, and
          houses, with explanations that are simple and easy to follow.
        </p>
      </div>

      <div className="uaa-card">
        <h3 className="uaa-h3">Explore More with AI Astrology</h3>
        <p>
          Whether you're checking your daily Horoscope, reading your Rashifal,
          understanding your Panchang, exploring Kundli matching, or learning
          about important planetary periods, Aura AI helps you continue the
          conversation through personalized AI astrology.
        </p>
        <p>
          Instead of searching across multiple websites, you can ask questions
          naturally and receive guidance that's connected to your own birth
          chart. Every tool lives on one page too: the{" "}
          <Link to="/free-astrology">free astrology</Link> hub lists the
          kundli, matching, calculators, horoscope and panchang in one place.
        </p>
      </div>

      <div className="uaa-card">
        <h3 className="uaa-h3">Why Choose Aura AI?</h3>
        <p>AI astrology should feel personal, simple, and easy to explore.</p>
        <p>
          Whether you're looking for an AI astrologer, a Kundli app, or an
          easier way to understand your birth chart, Aura AI brings together AI
          astrology and Vedic astrology in one seamless experience.
        </p>
        <p>
          Instead of trying to interpret complex charts on your own, you can ask
          questions naturally and receive thoughtful answers based on your
          Kundli.
        </p>
        <ul className="uaa-reasons">
          {REASONS.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export default UnderstandAiAstrology;
